import { Port } from 'cloud-networks/modules/PortsModule/PortsModule.types';
import { Router } from 'cloud-networks/modules/RoutersModule/RoutersModule.types';
import { SubnetApiEntity } from 'cloud-networks/modules/SubnetModule/SubnetModule.types';
import { BoundInfraServiceSelectors } from 'infra/modules/infraServiceModule';
import R from 'ramda';
import React from 'react';
import { networksUtils } from 'services/cloud-networks/modules/networksModule/networksUtils';
import router from 'system/router';

import { DropdownOption } from 'reactComponents/Dropdown/Dropdown';
import { SubnetOption } from 'reactComponents/SubnetOption/SubnetOption';
import { Tooltip } from 'reactComponents/Tooltip/Tooltip';

type NewNetworkOption = {
    value: string;
    key: string;
    text: string;
    href?: string;
};

const NEW_NETWORK_OPTION: NewNetworkOption = {
    value: 'new',
    key: 'new',
    text: 'Создать новую сеть',
};

type OptionsGetterRequiredParams = {
    networks: any[];
    subnets: SubnetApiEntity[];
    routers: Router[];
    ports: Port[];
};

const getOptions = ({
    includeAllNetworks,
    isExtNetItemsHidden,
    defaultOptions = [],
    networks,
    subnets,
    routers,
    ports,
    availableSubnetDescription,
    noInternetSubnetsDisabled,
    withNewNetwork,
    newNetworkLink,
    isNetworkSelectable,
    isSharedSubnetsDisabled,
}: {
    withNewNetwork?: boolean;
    newNetworkLink?: boolean;
    includeAllNetworks?: boolean;
    isExtNetItemsHidden?: boolean;
    defaultOptions?: DropdownOption[];
    availableSubnetDescription?: React.ReactNode;
    noInternetSubnetsDisabled?: boolean;
    isNetworkSelectable?: boolean;
    isSharedSubnetsDisabled?: boolean;
    // eslint-disable-next-line sonarjs/cognitive-complexity
} & OptionsGetterRequiredParams) => {
    let options: DropdownOption[] = [...defaultOptions];

    if (newNetworkLink) {
        NEW_NETWORK_OPTION.href = router.getUrl('#network-add');
    }

    if (withNewNetwork) {
        options = [NEW_NETWORK_OPTION, ...options];
    }

    const subnetsWithRouterConnectivity = networksUtils.getSubnetsWithRouterConnectivity(
        {
            networks,
            subnets,
            routers,
            ports,
        },
    );

    const connectedSubnetIds = subnetsWithRouterConnectivity.map(
        ({ id }) => id,
    );

    const availableNetworks = networks.filter(
        (network) => !networksUtils.isDefaultInternalNetwork(network.id),
    );
    const { internalNetworks = [], externalNetworks = [] } = R.groupBy(
        (network) => {
            return networksUtils.isInternalNetwork(network)
                ? 'internalNetworks'
                : 'externalNetworks';
        },
        availableNetworks,
    );
    const filteredNetworks = includeAllNetworks
        ? [...externalNetworks, ...internalNetworks]
        : internalNetworks;

    const externalNetworkIds = externalNetworks.map((item) => item.id);
    const tenantId = BoundInfraServiceSelectors.getOsProjectId();

    for (const network of filteredNetworks) {
        const items: DropdownOption[] = [];
        const isShared = network.tenant_id !== tenantId;

        for (const subnet of subnets) {
            if (
                subnet.network_id !== network.id ||
                (isExtNetItemsHidden &&
                    externalNetworkIds.includes(subnet.network_id))
            ) {
                continue;
            }
            const hasExternalConnection =
                includeAllNetworks || connectedSubnetIds.includes(subnet.id);

            let content: React.ReactNode = subnet.name;
            if (isSharedSubnetsDisabled && isShared) {
                content = getDisabledOptionWithTooltip(
                    subnet,
                    'Недостаточно прав, чтобы создать порт в этой подсети',
                );
            } else if (hasExternalConnection) {
                content = (
                    <SubnetOption
                        name={subnet.name || subnet.id}
                        description={availableSubnetDescription || subnet.cidr}
                    />
                );
            } else if (!noInternetSubnetsDisabled) {
                content = getDisabledOptionWithTooltip(
                    subnet,
                    'Чтобы выбрать данную сеть, создайте маршрутизатор к сети ext-net',
                );
            }

            items.push({
                value: subnet.id,
                key: subnet.id,
                content,
                disabled:
                    (isSharedSubnetsDisabled && isShared) ||
                    (!hasExternalConnection && !noInternetSubnetsDisabled),
            });
        }

        if (isNetworkSelectable || items.length) {
            options.push({
                key: network.id,
                text: networksUtils.getNetworkName(network),
                value: isNetworkSelectable ? network.id : undefined,
                description: network.private_dns_domain,
                items,
            });
        }
    }

    return options;
};

function getDisabledOptionWithTooltip(
    subnet: SubnetApiEntity,
    tooltipContent: React.ReactNode,
) {
    return (
        <Tooltip
            trigger={
                <SubnetOption
                    name={subnet.name || subnet.id}
                    description={subnet.cidr}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                />
            }
            content={tooltipContent}
            position="right center"
            basic
        />
    );
}

const getFirstAvailableSubnetId = ({
    networks,
    subnets,
    routers,
    ports,
}: OptionsGetterRequiredParams) => {
    const subnetsOptions = getOptions({
        networks,
        subnets,
        routers,
        ports,
    });
    let firstAvailableId;

    for (const item of subnetsOptions) {
        for (const subItem of item.items || []) {
            if (!subItem.disabled) {
                firstAvailableId = subItem.value;
                break;
            }
        }

        if (firstAvailableId) {
            break;
        }
    }

    return firstAvailableId;
};

const getFirstNotNewOption = (
    options: DropdownOption[],
): string | undefined => {
    return options
        .map((option) => (option?.items?.[0]?.value || option.value) as string)
        .find((value) => value !== NEW_NETWORK_OPTION.value);
};

export const FieldSubnetUtils = {
    getFirstAvailableSubnetId,
    getOptions,
    getFirstNotNewOption,
    NEW_NETWORK_OPTION,
};
